import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Switch } from "../ui/switch";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { ShieldCheck, ShieldAlert, FileSpreadsheet, GraduationCap, CheckCircle2, XCircle } from "lucide-react";
import { API_URL } from "../../lib/utils";

const Check = ({ ok, label, hint }) => (
  <div className="flex items-start gap-2 text-sm" data-testid={`fiscal-check-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
    {ok ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" /> : <XCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />}
    <div>
      <p className={ok ? "" : "font-medium"}>{label}</p>
      {!ok && hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  </div>
);

export const FiscalCompliancePanel = ({ settings, setSettings }) => {
  const [software, setSoftware] = useState(null);

  useEffect(() => {
    axios.get(`${API_URL}/fiscal/software`, { withCredentials: true }).then((r) => setSoftware(r.data)).catch(() => setSoftware(null));
  }, []);

  const nifOk = (settings.nif || "").replace(/[^0-9A-Za-z]/g, "").length >= 9;
  const addrOk = !!(settings.address || "").trim() && !!(settings.city || "").trim();
  const certified = software?.certified;

  return (
    <div className="space-y-4" data-testid="fiscal-compliance-panel">
      <Card className={certified ? "border-emerald-500/40" : "border-amber-500/40"}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {certified ? <ShieldCheck className="w-5 h-5 text-emerald-500" /> : <ShieldAlert className="w-5 h-5 text-amber-500" />}
            Conformidade fiscal — AGT (Decreto Presidencial n.º 312/18)
          </CardTitle>
          <CardDescription>
            Documentos assinados digitalmente (RSA), numeração sequencial por série, SAF-T (AO) validado pelo esquema oficial. A comunicação à AGT faz-se pelo ficheiro SAF-T do período — não existe submissão electrónica factura a factura.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="p-3 rounded-lg bg-muted/50">
              <p className="text-xs text-muted-foreground">N.º de validação AGT</p>
              <p className="text-xl font-bold font-mono" data-testid="fiscal-validation-number">{software?.validation_number ?? "…"}</p>
              <Badge variant={certified ? "default" : "secondary"} className="mt-1">{certified ? "Software validado" : "Aguarda validação da AGT"}</Badge>
            </div>
            <div className="p-3 rounded-lg bg-muted/50">
              <p className="text-xs text-muted-foreground">Produto / versão</p>
              <p className="font-mono text-sm">{software?.product_id}</p>
              <p className="font-mono text-sm">v{software?.product_version} · chave RSA v{software?.key_version}</p>
            </div>
            <div className="p-3 rounded-lg bg-muted/50">
              <p className="text-xs text-muted-foreground">Menção impressa nos documentos</p>
              <p className="font-mono text-xs break-all">XXXX-Processado por programa validado n.º {software?.validation_number ?? "0"}/AGT</p>
            </div>
          </div>

          <div className="space-y-2">
            <p className="text-sm font-medium">Dados obrigatórios do emitente</p>
            <Check ok={nifOk} label="NIF da empresa" hint="Preencha o NIF no separador Empresa — sem NIF não é possível emitir documentos" />
            <Check ok={addrOk} label="Morada e cidade" hint="Preencha morada e cidade no separador Empresa (obrigatório no cabeçalho do SAF-T)" />
            <Check ok={true} label="Preços com IVA incluído · motivos de isenção obrigatórios para produtos a 0 %" />
          </div>

          <div className="flex items-center justify-between p-4 rounded-lg border border-dashed">
            <div className="flex items-center gap-3">
              <GraduationCap className="w-6 h-6 text-muted-foreground" />
              <div>
                <Label>Modo de formação</Label>
                <p className="text-sm text-muted-foreground">Documentos emitidos na série FORM, marcados "DOCUMENTO DE FORMAÇÃO — SEM VALOR FISCAL". Desligue para operar.</p>
              </div>
            </div>
            <Switch checked={!!settings.training_mode} onCheckedChange={(v) => setSettings({ ...settings, training_mode: v })} className="data-[state=unchecked]:bg-slate-300 dark:data-[state=unchecked]:bg-slate-600" data-testid="fiscal-training-mode-switch" />
          </div>

          <div className="flex flex-wrap gap-3">
            <Button variant="outline" onClick={() => (window.location.href = "/saft")} className="gap-2" data-testid="fiscal-goto-saft-btn">
              <FileSpreadsheet className="w-4 h-4" /> SAF-T (AO), validação XSD e recuperação de documentos manuais
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
